import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../context";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../api/useFetchObjects";
import axios from "axios";
import { toast } from "react-toastify";
import { getHospitalName, getHospitalAddress } from "../../../utils/hospitalInfo";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Checkbox,
  Card,
  CardActionArea,
  CardContent,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Event as SeminarIcon,
  Print as PrintIcon,
  CheckBox as AllPresentIcon,
  CheckBoxOutlineBlank as AllAbsentIcon,
} from "@mui/icons-material";

/** Returns list of date strings (YYYY-MM-DD) for this seminar: single day or range seminar_date..end_date */
function getSeminarDates(seminar) {
  if (!seminar?.seminar_date) return [];
  const start = seminar.seminar_date;
  const end = seminar.end_date && seminar.end_date >= start ? seminar.end_date : null;
  if (!end) return [start];
  const dates = [];
  const d = new Date(start + "T12:00:00");
  const endD = new Date(end + "T12:00:00");
  while (d <= endD) {
    dates.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() + 1);
  }
  return dates;
}

export default function TakeAttendance() {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const location = useLocation();
  const preselectedId = location.state?.seminarId != null ? String(location.state.seminarId) : null;

  const [selectedSeminar, setSelectedSeminar] = useState(null);
  const [attendanceByDate, setAttendanceByDate] = useState({});
  const [saving, setSaving] = useState(false);

  const { data: seminarsData } = useFetchObjects(
    ["seminars-list"],
    "seminars/?page_size=500&ordering=-seminar_date",
    token
  );
  const { data: academicsData } = useFetchObjects(
    ["academics-active"],
    "academics/?page_size=500&status=1",
    token
  );
  const { data: existingData } = useFetchObjects(
    ["seminar-attendances-for", selectedSeminar?.id],
    selectedSeminar?.id ? `seminar-attendances/?seminar=${selectedSeminar.id}&page_size=1000` : "",
    token,
    { enabled: !!selectedSeminar?.id }
  );

  const seminars = useMemo(() => {
    const raw = seminarsData?.results ?? seminarsData;
    return Array.isArray(raw) ? raw : [];
  }, [seminarsData]);
  const academics = useMemo(() => {
    const raw = academicsData?.results ?? academicsData;
    return Array.isArray(raw) ? raw : [];
  }, [academicsData]);
  const existing = useMemo(() => {
    const raw = existingData?.results ?? existingData;
    return Array.isArray(raw) ? raw : [];
  }, [existingData]);

  const seminarDates = useMemo(
    () => (selectedSeminar ? getSeminarDates(selectedSeminar) : []),
    [selectedSeminar]
  );

  useEffect(() => {
    if (preselectedId && seminars.length > 0 && !selectedSeminar) {
      const s = seminars.find((s) => String(s.id) === String(preselectedId));
      if (s) setSelectedSeminar(s);
    }
  }, [preselectedId, seminars, selectedSeminar]);

  useEffect(() => {
    if (!selectedSeminar || seminarDates.length === 0) return;
    const byDate = {};
    seminarDates.forEach((dateStr) => {
      byDate[dateStr] = {};
      academics.forEach((a) => {
        byDate[dateStr][a.id] = false;
      });
    });
    existing.forEach((r) => {
      const dateKey = r.attendance_date || selectedSeminar.seminar_date;
      if (byDate[dateKey] && r.academic != null) {
        byDate[dateKey][r.academic] = !!r.attended;
      }
    });
    setAttendanceByDate(byDate);
  }, [selectedSeminar, seminarDates, academics, existing]);

  const handleToggle = (dateStr, academicId) => {
    setAttendanceByDate((prev) => ({
      ...prev,
      [dateStr]: {
        ...(prev[dateStr] || {}),
        [academicId]: !(prev[dateStr] && prev[dateStr][academicId]),
      },
    }));
  };

  const markAllForDate = (dateStr, value) => {
    setAttendanceByDate((prev) => {
      const next = { ...prev };
      next[dateStr] = { ...(next[dateStr] || {}) };
      academics.forEach((a) => {
        next[dateStr][a.id] = value;
      });
      return next;
    });
  };

  const handleSave = async () => {
    if (!selectedSeminar) return;
    setSaving(true);
    try {
      for (const dateStr of seminarDates) {
        const map = attendanceByDate[dateStr] || {};
        const attendances = academics.map((a) => ({
          academic: a.id,
          attended: !!map[a.id],
        }));
        await axios.post(
          `${process.env.REACT_APP_API_URL}/seminar-attendances/take-attendance/`,
          { seminar: selectedSeminar.id, date: dateStr, attendances },
          {
            headers: token ? { Authorization: `Token ${token}` } : {},
            skipGlobalErrorHandler: true,
          }
        );
      }
      toast.success(t("academic.attendanceSaved", "Attendance saved."));
    } catch (err) {
      const msg =
        err.response?.data?.detail || err.message || t("academic.saveFailed", "Failed to save.");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => setSelectedSeminar(null);

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error(t("academic.printBlocked", "Pop-up blocked. Allow pop-ups to print."));
      return;
    }
    const hospitalName = getHospitalName(t);
    const hospitalAddress = getHospitalAddress(t);
    const seminarTitle = selectedSeminar?.title || "";
    const dateRangeText =
      seminarDates.length > 1
        ? `${selectedSeminar?.seminar_date} – ${selectedSeminar?.end_date} (${seminarDates.length} ${t("academic.days", "days")})`
        : selectedSeminar?.seminar_date || "";

    const headerCells = seminarDates
      .map(
        (d, i) =>
          `<th class="date-col ${i % 2 === 0 ? "date-col-even" : "date-col-odd"}">${d}</th>`
      )
      .join("");
    const rows = academics
      .map((a, idx) => {
        const { present, absent } = getPresentAbsentForAcademic(a.id);
        const name =
          a.name_display ||
          `${a.first_name || ""} ${a.last_name || ""}`.trim() ||
          a.trainee_id ||
          a.id;
        const traineeId = a.trainee_id || "—";
        const dateCells = seminarDates
          .map((dateStr, i) => {
            const attended = !!attendanceByDate[dateStr]?.[a.id];
            const cellClass = `date-cell ${i % 2 === 0 ? "date-cell-even" : "date-cell-odd"}`;
            return `<td class="${cellClass}">${attended ? "P" : "A"}</td>`;
          })
          .join("");
        const rowClass = idx % 2 === 0 ? "row-even" : "row-odd";
        return `<tr class="${rowClass}">
          <td class="col-num">${idx + 1}</td>
          <td class="col-name">${name}</td>
          <td class="col-id">${traineeId}</td>
          ${dateCells}
          <td class="col-present">${present}</td>
          <td class="col-absent">${absent}</td>
        </tr>`;
      })
      .join("");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>${t("academic.attendanceSheet", "Attendance sheet")} - ${seminarTitle}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; margin: 0; color: #222; }
            .hospital-block {
              text-align: center;
              padding: 20px 0 24px 0;
              border-bottom: 2px solid #006064;
              margin-bottom: 20px;
            }
            .hospital-block h1 { margin: 0 0 8px 0; font-size: 22px; font-weight: 700; color: #006064; }
            .hospital-block .address { margin: 0; font-size: 13px; color: #555; }
            .seminar-title { font-size: 18px; font-weight: 700; margin: 0 0 4px 0; color: #333; }
            .seminar-meta { font-size: 13px; color: #666; margin-bottom: 16px; }
            table { border-collapse: collapse; width: 100%; font-size: 12px; }
            th, td { border: 1px solid #b0bec5; padding: 8px 6px; text-align: center; }
            th { font-weight: 700; color: #fff; background: #006064; }
            th.col-num { width: 32px; }
            th.col-name { min-width: 140px; text-align: left; }
            th.col-id { min-width: 90px; text-align: left; }
            th.date-col { min-width: 36px; font-size: 11px; }
            th.col-present { background: #2e7d32; min-width: 56px; }
            th.col-absent { background: #c62828; min-width: 56px; }
            td.col-num { width: 32px; font-weight: 600; color: #555; }
            td.col-name { text-align: left; min-width: 140px; }
            td.col-id { text-align: left; min-width: 90px; color: #555; }
            td.date-cell { min-width: 36px; font-weight: 600; }
            td.date-cell-even { background: #fff8e1; }
            td.date-cell-odd { background: #e3f2fd; }
            td.col-present { font-weight: 700; background: rgba(46, 125, 50, 0.15); }
            td.col-absent { font-weight: 700; background: rgba(198, 40, 40, 0.12); }
            tr.row-even td.col-num, tr.row-even td.col-name, tr.row-even td.col-id { background: #fff; }
            tr.row-odd td.col-num, tr.row-odd td.col-name, tr.row-odd td.col-id { background: #e3f2fd; }
            @page { size: landscape; }
            @media print {
              body { padding: 12px; }
              .hospital-block { padding: 12px 0 16px 0; }
            }
          </style>
        </head>
        <body>
          <div class="hospital-block">
            <h1>${hospitalName}</h1>
            <p class="address">${hospitalAddress}</p>
          </div>
          <div class="seminar-title">${seminarTitle}</div>
          <div class="seminar-meta">${dateRangeText}</div>
          <table>
            <thead>
              <tr>
                <th class="col-num">#</th>
                <th class="col-name">${t("academic.participant", "Participant")}</th>
                <th class="col-id">${t("academic.traineeId", "Trainee ID")}</th>
                ${headerCells}
                <th class="col-present">${t("academic.present", "Present")}</th>
                <th class="col-absent">${t("academic.absent", "Absent")}</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const getPresentAbsentForAcademic = (academicId) => {
    let present = 0;
    let absent = 0;
    seminarDates.forEach((dateStr) => {
      const attended = attendanceByDate[dateStr]?.[academicId];
      if (attended) present++;
      else absent++;
    });
    return { present, absent };
  };

  return (
    <Box sx={{ p: 3, width: "100%", maxWidth: 1200, mx: "auto" }}>
      {!selectedSeminar ? (
        <>
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold", mb: 2, display: "flex", alignItems: "center", gap: 1 }}
          >
            <SeminarIcon color="primary" />
            {t("academic.takeAttendance", "Take attendance")}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {t(
              "academic.clickSeminarToMark",
              "Click a seminar to see participants and mark them present or absent for each day."
            )}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {seminars.length === 0 ? (
              <Typography color="text.secondary">
                {t("academic.noSeminars", "No seminars yet. Create seminars first.")}
              </Typography>
            ) : (
              seminars.map((s) => {
                const dates = getSeminarDates(s);
                const dateLabel =
                  dates.length > 1
                    ? `${s.seminar_date} – ${s.end_date} (${dates.length} ${t("academic.days", "days")})`
                    : s.seminar_date;
                return (
                  <Card
                    key={s.id}
                    sx={{ minWidth: 280, flex: "1 1 280px", maxWidth: 360 }}
                  >
                    <CardActionArea onClick={() => setSelectedSeminar(s)} sx={{ p: 2 }}>
                      <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                          {s.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {dateLabel}
                        </Typography>
                        {s.location && (
                          <Typography variant="body2" color="text.secondary">
                            {s.location}
                          </Typography>
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })
            )}
          </Box>
        </>
      ) : (
        <Paper sx={{ p: 3 }} elevation={2}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 2 }}>
            <Button startIcon={<BackIcon />} onClick={handleBack}>
              {t("academic.backToSeminars", "Back to seminars")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{ ml: 1 }}
            >
              {t("common.print", "Print")}
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ ml: "auto" }}
            >
              {saving ? t("common.saving", "Saving...") : t("academic.saveAttendance", "Save attendance")}
            </Button>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 0.5 }}>
            {selectedSeminar.title}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {seminarDates.length > 1
              ? `${selectedSeminar.seminar_date} – ${selectedSeminar.end_date} (${seminarDates.length} ${t("academic.days", "days")})`
              : selectedSeminar.seminar_date}
          </Typography>

          {academics.length === 0 ? (
            <Typography color="text.secondary">
              {t("academic.noActiveTrainees", "No active trainees. Add academics with status Active.")}
            </Typography>
          ) : (
            <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.100" }}>
                    <TableCell sx={{ fontWeight: "bold", minWidth: 48 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: "bold", minWidth: 180 }}>
                      {t("academic.participant", "Participant")}
                    </TableCell>
                    {seminarDates.map((dateStr) => (
                      <TableCell key={dateStr} align="center" sx={{ fontWeight: "bold", minWidth: 100 }}>
                        {dateStr}
                        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                          <Button
                            size="small"
                            startIcon={<AllPresentIcon />}
                            onClick={() => markAllForDate(dateStr, true)}
                            sx={{ minWidth: 0, px: 0.5 }}
                            title={t("academic.allPresent", "All present")}
                          />
                          <Button
                            size="small"
                            startIcon={<AllAbsentIcon />}
                            onClick={() => markAllForDate(dateStr, false)}
                            sx={{ minWidth: 0, px: 0.5 }}
                            title={t("academic.allAbsent", "All absent")}
                          />
                        </Box>
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ fontWeight: "bold", bgcolor: "rgba(46, 125, 50, 0.2)", minWidth: 80 }}>
                      {t("academic.present", "Present")}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold", bgcolor: "rgba(211, 47, 47, 0.2)", minWidth: 80 }}>
                      {t("academic.absent", "Absent")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {academics.map((a, idx) => {
                    const { present, absent } = getPresentAbsentForAcademic(a.id);
                    return (
                      <TableRow key={a.id} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          {a.name_display || `${a.first_name || ""} ${a.last_name || ""}`.trim() || a.trainee_id || a.id}
                          {a.trainee_id && (
                            <Typography component="span" variant="caption" color="text.secondary" display="block">
                              {a.trainee_id}
                            </Typography>
                          )}
                        </TableCell>
                        {seminarDates.map((dateStr) => {
                          const map = attendanceByDate[dateStr] || {};
                          const checked = !!map[a.id];
                          return (
                            <TableCell key={dateStr} align="center" padding="checkbox">
                              <Checkbox
                                checked={checked}
                                onChange={() => handleToggle(dateStr, a.id)}
                                color="primary"
                              />
                            </TableCell>
                          );
                        })}
                        <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "rgba(46, 125, 50, 0.15)" }}>
                          {present}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "rgba(211, 47, 47, 0.15)" }}>
                          {absent}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Box>
  );
}
